import { useState } from 'react';
import { TextField, Grid, Box, Stack, Typography } from '@mui/material';
import {
	Abc as AbcIcon,
	AlternateEmail as AlternateEmailIcon,
	People as PeopleIcon,
	Event as EventIcon,
	Message as MessageIcon,
	Tour as TourIcon
} from '@mui/icons-material';
import GeneralDialog from "components/shared/GeneralDialog";
import SendButton from "components/shared/SendButton";
import PhoneField from "components/shared/FormFields/PhoneFormField";

import { useActivityShopUI } from "hooks/useActivityShopUI";
import { useLocalization } from "hooks/useLocalization";
import { useBusiness } from "hooks/useBusiness";
import { getSingularOrPluralCode } from "utils/formatUtils";

import { AvailableDatesMonth } from 'interfaces/ActivityShopUIInterfaces';

interface LowAvailabilityDialogProps {
	isOpen: boolean;
	onClose: () => void;
	errorType: 'soldOut' | 'insufficientAvailability' | null;
	errorDate: Date | null;
	activityId: number;
	selectedMonthData: AvailableDatesMonth | null;
}

function LowAvailabilityDialog({ isOpen, onClose, errorType, errorDate, activityId, selectedMonthData }: LowAvailabilityDialogProps) {
	const { getStr } = useLocalization();

	const dialogTitle = errorType === 'soldOut' ? getStr('Sold_out') : getStr('Low_availability');

	return (
		<GeneralDialog 
			isOpen={isOpen}
			onClose={() => {onClose()}}
			title={
				dialogTitle
			}
			content={
				<LowAvailabilityDialogContent
					errorType={errorType}
					errorDate={errorDate}
					activityId={activityId}
					selectedMonthData={selectedMonthData}
					onClose={onClose}
				/>
			}
			type={'error'}
		/>
	);
}

interface LowAvailabilityDialogContentProps {
	errorType: 'soldOut' | 'insufficientAvailability' | null;
	errorDate: Date | null;
	activityId: number;
	selectedMonthData: AvailableDatesMonth | null;
	onClose: () => void;
}

function LowAvailabilityDialogContent({ errorType, errorDate, activityId, selectedMonthData, onClose }: LowAvailabilityDialogContentProps) {
	const [isSending, setIsSending] = useState<boolean>(false);
	const [isSent, setIsSent] = useState<boolean>(false);
	const [showSendError, setShowSendError] = useState<boolean>(false);
	const [sendErrorMessage, setSendErrorMessage] = useState<string>('');
	const { getTotalPaxQty, paxArr } = useActivityShopUI();
	const { getStr, langCode } = useLocalization();
	const { activities, getBizConfigVar, businessSlug, businessToken, sendRequest } = useBusiness();
	const [name, setName] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [countryCode, setCountryCode] = useState<string>(getBizConfigVar('countryCode'));
	const [phone, setPhone] = useState<string>('');
	const [message, setMessage] = useState<string>('');
	const [touched, setTouched] = useState({ name: false, email: false });
	const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
	const [isFormValid, setIsFormValid] = useState<boolean>(false);

	const MAX_MESSAGE_LENGTH = 500;
	const bizContactEmails = getBizConfigVar('bizContactEmails');

	//Get activity title
	const activity = activities.find(activity => activity.id === activityId);
	const activityTitle = activity ? activity.title : '';

	const totalPaxQty = getTotalPaxQty(paxArr);

	//Get date in localized format from errorDate Date object
	const dateStr = errorDate ? errorDate.toLocaleDateString('en-US', {
        month: 'long', // "short" month format, e.g., "Jan"
        day: 'numeric', // Numeric day of the month
        year: 'numeric' // Numeric full year
    }) : '';
	
    let highestDepartureAvailability = { departureTime: '', availability: 0 };

	if (errorType === 'insufficientAvailability') {
		const errorDateDay = errorDate ? errorDate.getDate() : null;
		const dayDepartures = selectedMonthData ? selectedMonthData.days.find(day => day.dayNum === errorDateDay)?.departures : null;
	
		if (dayDepartures) {
			dayDepartures.forEach(departure => {
				const departureAvailability = departure.groups.reduce((acc, group) => acc + (group.capacity - group.occupancy), 0);
				if (departureAvailability > highestDepartureAvailability.availability) {
					highestDepartureAvailability = { departureTime: departure.time, availability: departureAvailability };
				}
			});
		}
	}

	const handleBlur = (field: string) => {
		setTouched(prev => ({ ...prev, [field]: true }));
	};

	interface checkFormValidityProps {
		name: string;
		email: string;
		phone: string;
	}

	const checkFormValidity = ({ name, email, phone }: checkFormValidityProps) => {
		const isEmailValid = validateEmail(email);
		setIsEmailValid(isEmailValid);
		setIsFormValid(!!name && isEmailValid && !!phone);
	};

	const validateEmail = (email: string) => {
		const re = /^[_a-z0-9-]+(\.[_a-z0-9-]+)*(\+[a-z0-9-]+)?@[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
		return re.test(String(email).toLowerCase());
	};

	const handleNameChange = (newName: string) => {
		setName(newName);

		checkFormValidity({
			name: newName,
			email,
			phone
		});
	};

	const handleEmailChange = (newEmail: string) => {
		setEmail(newEmail);

		checkFormValidity({
			name,
			email: newEmail,
			phone
		});
	};

	const handleCountryCodeChange = (newCountryCode: string) => {
		setCountryCode(newCountryCode);
	};

	const handlePhoneChange = (newPhone: string) => {
		setPhone(newPhone);

		checkFormValidity({
			name,
			email,
			phone: newPhone
		});
	};

	const handleMessageChange = (newMessage: string) => {
		if (newMessage.length <= MAX_MESSAGE_LENGTH) {
			setMessage(newMessage);
		}
	};

	const handleSend = () => {
		setIsSending(true);

		sendRequest({
			type: 'spaceRequest',
			params: {
				activityId: activityId,
				dateYmd: errorDate?.toISOString().slice(0, 10),
				customer: {
					lang: langCode,
					name: name,
					paxQty: totalPaxQty,
					email: email,
					countryCode: countryCode,
					phone: phone,
					message: message
				}
			},
			onSuccess: (data) => {
				setIsSent(true);
			},
			onError: (error) => {
				setSendErrorMessage(getStr('Error') + ': ' + error.message);
				setShowSendError(true);
			},
			onFinally: () => {
				setIsSending(false);
			}
		});
	};

	const onErrorClose = () => {
		setShowSendError(false);
	};

	const onSentMsgClose = () => {
		setIsSent(false);
		onClose();
	};

	return(
		<>
			<Stack spacing={1}>
				<Typography>
					{
						errorType === 'soldOut' ?
						getStr('LOW_AVAILABILITY_DIALOG_SOLD_OUT_1', dateStr) :
						getStr(
							'LOW_AVAILABILITY_DIALOG_INSUFFICIENT_1',
							dateStr,
							String(highestDepartureAvailability.availability),
							getSingularOrPluralCode(highestDepartureAvailability.availability, getStr('person'), getStr('people')),
							String(totalPaxQty),
							getSingularOrPluralCode(totalPaxQty, getStr('person'), getStr('people'))
						)
					}
				</Typography>
				{
					bizContactEmails.length > 0 && (
						<Typography>
							{getStr('LOW_AVAILABILITY_DIALOG_REQUEST_SPACE_INFO')}
						</Typography>
					)
				}
			</Stack>
			{
				bizContactEmails.length > 0 && (
					<>
						<Grid sx={{mt: 1}} spacing={2} container>
							<Grid item xs={12}>
								<Box display="flex" alignItems="center">
									<TourIcon sx={{ mr: 1 }} />
									<Typography sx={{ ml: 2 }}>
										{activityTitle}
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={12}>
								<Box display="flex" alignItems="center">
									<EventIcon sx={{ mr: 1 }} />
									<Typography sx={{ ml: 2 }}>
										{dateStr}
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={12}>
								<Box display="flex" alignItems="center">
									<PeopleIcon sx={{ mr: 1 }} />
									<Typography sx={{ ml: 2 }}>
										{totalPaxQty} {getSingularOrPluralCode(totalPaxQty, getStr('person'), getStr('people'))}
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={12}>
								<Box display="flex" alignItems="center">
									<AbcIcon sx={{ mr: 1 }} />
									<TextField
										label={getStr('Name')}
										value={name}
										onBlur={() => {handleBlur('name')}}
										variant="outlined"
										required
										sx={{ width: '100%' }}
										onChange={(e) => {handleNameChange(e.target.value)}}
										error={touched.name && name.length === 0}
										disabled={isSending}
									/>
								</Box>
							</Grid>
							<Grid item xs={12}>
								<Box display="flex" alignItems="center">
									<AlternateEmailIcon sx={{ mr: 1 }} />
									<TextField
										label={getStr('Email')}
										value={email}
										onBlur={() => {handleBlur('email')}}
										variant="outlined"
										required
										sx={{ width: '100%' }}
										onChange={(e) => {handleEmailChange(e.target.value.trim())}}
										error={touched.email && !isEmailValid}
										disabled={isSending}
									/>
								</Box>
							</Grid>
							<Grid item xs={12}>
								<PhoneField
									countryCode={countryCode}
									onCountryCodeChange={handleCountryCodeChange}
									phone={phone}
									onPhoneChange={handlePhoneChange}
									disabled={isSending}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box display="flex" alignItems="center">
									<MessageIcon sx={{ mr: 1 }} />
									<TextField
										label={getStr('Message')}
										value={message}
										variant="outlined"
										multiline
										rows={3}
										inputProps={{
											maxLength: MAX_MESSAGE_LENGTH
										}}
										sx={{ width: '100%' }}
										onChange={(e) => {handleMessageChange(e.target.value)}}
										disabled={isSending}
									/>
								</Box>
							</Grid>
							<Grid item xs={12}>
								<SendButton
									onClick={handleSend}
									isValid={isFormValid}
									isLoading={isSending}
									label={getStr('Send')}
								/>
							</Grid>
						</Grid>
						<GeneralDialog 
							isOpen={showSendError}
							onClose={() => {onErrorClose()}}
							title={getStr('Error')}
							content={sendErrorMessage}
							type={'error'}
						/>
						<GeneralDialog 
							isOpen={isSent}
							onClose={() => {onSentMsgClose()}}
							title={getStr('Message_sent')}
							content={getStr('SPACE_REQUEST_WE_HAVE_RECEIVED_REQUEST_INFO')}
						/>
					</>
				)
			}
		</>
	);
}

export default LowAvailabilityDialog;
